import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listBlog } from "@/lib/api";
import { ArrowRight, Calendar, ChevronLeft, ChevronRight, Search } from "lucide-react";
import Seo, { ORG_SCHEMA, SITE_URL } from "@/components/Seo";

const PAGE_SIZE = 6;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    listBlog().then(setPosts).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category));
    return ["All", ...Array.from(set)];
  }, [posts]);

  const filtered = useMemo(() => {
    let list = posts;
    if (category !== "All") list = list.filter((p) => p.category === category);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((p) => p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q));
    }
    return list;
  }, [posts, category, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [category, query]);

  return (
    <div data-testid="blog-page">
      <Seo
        title="Blog"
        description="Latest news, festival guides and technology write-ups from Digi Hub Solution — IT security, digital marketing insights, and cultural stories from India."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Digi Hub Solution Blog",
          url: `${SITE_URL}/blog`,
          publisher: { "@id": ORG_SCHEMA["@id"] },
          blogPost: posts.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `${SITE_URL}/blog/${p.slug}`,
            image: p.image,
            datePublished: p.created_at,
          })),
        }}
      />

      <section className="hero-gradient text-white">
        <div className="container-x py-20 md:py-24 text-center">
          <div className="chip-light mx-auto">Blog</div>
          <h1 className="mt-4 text-4xl md:text-6xl">Ideas, culture & <span className="text-[color:var(--dh-orange)]">tech notes.</span></h1>
          <p className="mt-5 text-white/80 max-w-xl mx-auto leading-relaxed">
            Latest news, festival guides and technology write-ups from the Digi Hub Solution team.
          </p>
        </div>
      </section>

      <section className="container-x py-12">
        {/* Filter + search bar */}
        <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between mb-8" data-testid="blog-filters">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  category === c
                    ? "bg-[color:var(--dh-navy)] text-white"
                    : "bg-white text-[color:var(--dh-navy)] border border-[color:var(--dh-line)] hover:border-[color:var(--dh-orange)]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--dh-slate)]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search posts..."
              data-testid="blog-search"
              className="w-full pl-9 pr-4 py-2.5 rounded-full border border-[color:var(--dh-line)] bg-white text-sm focus:outline-none focus:border-[color:var(--dh-orange)]"
            />
          </div>
        </div>

        {(() => {
          if (loading) return <div className="text-center text-[color:var(--dh-slate)] py-12">Loading…</div>;
          if (paged.length === 0) return (
            <div className="text-center py-16 border border-dashed border-[color:var(--dh-line)] rounded-2xl">
              <div className="text-[color:var(--dh-slate)]">No posts match your filters.</div>
              <button onClick={() => { setCategory("All"); setQuery(""); }} className="btn-ghost-dark mt-4">Clear filters</button>
            </div>
          );
          return (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paged.map((p) => (
                <Link to={`/blog/${p.slug}`} key={p.slug} className="post-card group block rounded-2xl overflow-hidden border border-[color:var(--dh-line)] bg-white" data-testid={`blog-card-${p.slug}`}>
                  <div className="overflow-hidden aspect-[16/10]">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-[color:var(--dh-slate)]">
                      <span className="chip">{p.category}</span>
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {p.date}</span>
                    </div>
                    <h3 className="mt-3 font-display text-xl text-[color:var(--dh-navy)] group-hover:text-[color:var(--dh-orange)] transition-colors">{p.title}</h3>
                    <p className="mt-2 text-sm text-[color:var(--dh-slate)] line-clamp-3">{p.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--dh-navy)]">Read more <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-12 flex items-center justify-center gap-2" data-testid="blog-pagination">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="page-prev"
                  className="w-10 h-10 rounded-full border border-[color:var(--dh-line)] grid place-items-center disabled:opacity-40 hover:border-[color:var(--dh-orange)]"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    data-testid={`page-${n}`}
                    className={`w-10 h-10 rounded-full font-semibold text-sm ${
                      n === page
                        ? "bg-[color:var(--dh-orange)] text-white"
                        : "border border-[color:var(--dh-line)] text-[color:var(--dh-navy)] hover:border-[color:var(--dh-orange)]"
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="page-next"
                  className="w-10 h-10 rounded-full border border-[color:var(--dh-line)] grid place-items-center disabled:opacity-40 hover:border-[color:var(--dh-orange)]"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
          );
        })()}
      </section>
    </div>
  );
}
